import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PaymentProviderSelectorComponent } from '../../shared/design-system/payment-provider-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { PAYMENTS_LIST_PAGE_STYLES, paymentStatusLabelKey } from './payments-list-page.utils';
import { PaymentIntentRow, PaymentsAdminService } from './payments-admin.service';

@Component({
  standalone: true,
  selector: 'ain-payments-intents',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    SelectModule,
    TagModule,
    AinCanAccessDirective,
    CurrencySelectorComponent,
    PaymentProviderSelectorComponent,
    AinAmountPipe
  ],
  template: `
    <ain-page [eyebrow]="t('payments.eyebrow')" [title]="t('payments.intents.title')" [description]="t('payments.intents.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'payments.intents.help.page.title'"
        [descriptionKey]="'payments.intents.help.page.description'"
        [bulletKeys]="[
          'payments.intents.help.page.bullets.create',
          'payments.intents.help.page.bullets.filters',
          'payments.intents.help.page.bullets.status'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('payments.intents.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('payments.intents.unavailable')" [message]="error() ?? ''" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button
              pButton
              type="button"
              [label]="t('common.create')"
              ainCanAccess
              [ainCanAccessPermission]="'payments.intents'"
              (click)="openCreateDialog()">
            </button>
            <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="reload()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('payments.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'payments.intents.help.filters.title'"
                [descriptionKey]="'payments.intents.help.filters.description'" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('payments.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('payments.intents.filters.status') }}</span>
                <p-select
                  [options]="statusFilterOptions()"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  name="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          <div class="section-title">
            <span>{{ t('payments.intents.help.table.title') }}</span>
            <ain-info-hint
              [titleKey]="'payments.intents.help.table.title'"
              [descriptionKey]="'payments.intents.help.table.description'" />
          </div>
          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('payments.intents.states.empty')" [message]="t('payments.intents.states.emptyMessage')" />
          } @else {
            <p-table [value]="filteredRows()" dataKey="id" [paginator]="true" [rows]="10">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('payments.intents.externalReference') }}</th>
                  <th>{{ t('payments.intents.providerKey') }}</th>
                  <th>{{ t('payments.intents.amount') }}</th>
                  <th>{{ t('columns.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.externalReference }}</td>
                  <td>{{ row.providerKey }}</td>
                  <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                  <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (actionError()) { <p class="error">{{ actionError() }}</p> }
        </section>
      }

      <p-dialog
        [header]="t('payments.intents.createTitle')"
        [modal]="true"
        [(visible)]="createDialogVisible"
        [style]="{ width: '560px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.intents.providerKey') }}
              <ain-info-hint
                [titleKey]="'payments.intents.help.create.title'"
                [descriptionKey]="'payments.intents.help.create.description'" />
            </span>
            <ain-payment-provider-selector [(ngModel)]="createModel.providerKey" name="createProviderKey" [enabledOnly]="true" />
          </label>
          <label>
            <span>{{ t('payments.intents.externalReference') }}</span>
            <input pInputText [(ngModel)]="createModel.externalReference" name="createExternalReference" />
          </label>
          <label>
            <span>{{ t('payments.intents.idempotencyKey') }}</span>
            <input pInputText [(ngModel)]="createModel.idempotencyKey" name="createIdempotencyKey" />
          </label>
          <label>
            <span>{{ t('payments.intents.amount') }}</span>
            <input pInputText type="number" [(ngModel)]="createModel.amount" name="createAmount" />
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="createModel.currencyCode" name="createCurrencyCode" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="creating()" (click)="createIntent()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .label-row { display: inline-flex; align-items: center; gap: 6px; }
    ${PAYMENTS_LIST_PAGE_STYLES}
  `]
})
export class PaymentsIntentsComponent implements OnInit {
  private readonly api = inject(PaymentsAdminService);
  private readonly localizationService = inject(LocalizationService);

  readonly rows = signal<PaymentIntentRow[]>([]);
  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly search = signal('');
  statusFilter: string | null = null;
  createDialogVisible = false;
  createModel = { providerKey: '', externalReference: '', idempotencyKey: '', amount: 0, currencyCode: 'IRR' };

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.actionError.set(null);
    this.api.listIntents().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('payments.intents.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  statusFilterOptions(): { value: string; labelKey: string }[] {
    const statuses = [...new Set(this.rows().map(row => row.status).filter(Boolean))].sort();
    return statuses.map(value => ({ value, labelKey: paymentStatusLabelKey(value) }));
  }

  filteredRows(): PaymentIntentRow[] {
    const value = this.search().trim().toLowerCase();
    return this.rows()
      .filter(row => !this.statusFilter || row.status === this.statusFilter)
      .filter(row =>
        !value ||
        row.externalReference.toLowerCase().includes(value) ||
        row.providerKey.toLowerCase().includes(value) ||
        row.idempotencyKey.toLowerCase().includes(value) ||
        this.statusLabel(row.status).toLowerCase().includes(value));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
  }

  openCreateDialog(): void {
    this.createModel = { providerKey: '', externalReference: '', idempotencyKey: '', amount: 0, currencyCode: 'IRR' };
    this.actionError.set(null);
    this.createDialogVisible = true;
  }

  createIntent(): void {
    const payload = {
      providerKey: this.createModel.providerKey.trim(),
      externalReference: this.createModel.externalReference.trim(),
      idempotencyKey: this.createModel.idempotencyKey.trim(),
      amount: Number(this.createModel.amount),
      currencyCode: this.createModel.currencyCode.trim()
    };
    if (!payload.providerKey || !payload.externalReference || !payload.idempotencyKey || payload.amount <= 0 || !payload.currencyCode) {
      return;
    }

    this.creating.set(true);
    this.api.createIntent(payload).subscribe({
      next: created => {
        this.rows.update(rows => [created, ...rows]);
        this.createModel.externalReference = '';
        this.createModel.idempotencyKey = '';
        this.createModel.amount = 0;
        this.creating.set(false);
        this.createDialogVisible = false;
      },
      error: () => {
        this.creating.set(false);
        this.actionError.set(this.t('payments.intents.errorCreate'));
      }
    });
  }

  statusLabel(status: string): string {
    const key = paymentStatusLabelKey(status);
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    const normalized = status.toLowerCase();
    if (normalized.includes('fail') || normalized.includes('cancel') || normalized.includes('declin')) {
      return 'danger';
    }
    if (normalized.includes('pending') || normalized.includes('draft') || normalized.includes('created')) {
      return 'warn';
    }
    if (normalized.includes('capture') || normalized.includes('complete') || normalized.includes('approve')) {
      return 'success';
    }
    return 'info';
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
